import { Observable, throwError, of } from "rxjs";
import { retryWhen, mergeMap, delay } from "rxjs/operators";
import { HttpRequestOptions, IHttpErrorResponse } from "@schema/http";
import { HttpResponse } from "@angular/common/http";

const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_BACKOFF_DELAY = 500;
const	DEFAULT_IGNORED_STATUS_CODES = [401, 403];

/**
 * Retry request until success, max retry limit has been hit, or certain error codes are returned
 */
export function retryWithBackoff({
  retryDelayMs: delayMs = 0,
  retryMaxAttempts: maxRetry = DEFAULT_MAX_RETRIES,
  retryBackoffMs: backoffMs = DEFAULT_BACKOFF_DELAY,
  retryExcludedCodes: excludedCodes = DEFAULT_IGNORED_STATUS_CODES
}: Partial<HttpRequestOptions> = {}) {

  // number of retries left before returning an error
  let retriesLeft = maxRetry;

  return (src: Observable<HttpResponse<any>>) =>
    src.pipe(
      retryWhen((errors: Observable<HttpResponse<any>>) => errors.pipe(
        mergeMap(response => {

          // For status codes that we don't care to retry
          if (excludedCodes.find(e => e === response.status)) {
            return throwError({
              status: response.status,
              message: response.body,
            } as IHttpErrorResponse);
          }

          // Keep trying until we're out of retries
          if (retriesLeft-- > 0) {
            let backoffTime: number;
            if (response.headers && response.headers.has("Retry-After")) {
              // If the server sent a retry-after time, honor that
              backoffTime = parseInt(response.headers.get("Retry-After"), 10) * 1000;
            } else {
              // If they didn't, use our exponential backoff instead
              backoffTime = delayMs + ((maxRetry - retriesLeft) * backoffMs);
            }
            return of(response).pipe(delay(backoffTime));
          }

          // Out of retries, wah wah
          return throwError({
            status: response.status,
            message: `Tried to load resource ${maxRetry + 1} times without success.`,
          } as IHttpErrorResponse);
        })
      ))
    );
}
